import React from 'react';
import { Link } from 'react-router-dom';
import TetfundLogo from '../../assets/images/TetfundLogo.svg';
import {
  ArrowLeft, BookOpen, Newspaper, UserPlus, MailCheck, LogIn,
  FileText, CheckCircle2,
} from 'lucide-react';

interface Step {
  title: string;
  desc: string;
}

const signupSteps: (Step & { icon: React.ReactNode })[] = [
  {
    icon: <UserPlus size={20} />,
    title: 'Create an account',
    desc: 'Click "Create account" and choose your user type: Manuscript Author or Professional Journal Representative. Fill in your account, personal and contact details.',
  },
  {
    icon: <FileText size={20} />,
    title: 'Add your professional information',
    desc: 'Authors provide their affiliation, institution, highest qualification and academic rank. Journal representatives provide their affiliation details. Then set a password and accept the terms.',
  },
  {
    icon: <MailCheck size={20} />,
    title: 'Verify your email',
    desc: 'A verification link is sent to your email address. Open it to activate your account. Already registered under the other role? Enter the same email and the new role is added to your existing account.',
  },
  {
    icon: <LogIn size={20} />,
    title: 'Sign in and complete your profile',
    desc: 'Sign in with your email and password. You will be asked to complete your profile and bank details before you can submit.',
  },
];

const authorSteps: Step[] = [
  { title: 'Manuscript Info', desc: 'Enter the manuscript title, select a thematic area and list all contributing authors.' },
  { title: 'Upload File', desc: 'Upload your manuscript as a .doc or .docx file (max. 20MB).' },
  { title: 'Review & Submit', desc: 'Check your details and submit. Track its progress from "My Submissions" on your dashboard.' },
];

const journalSteps: Step[] = [
  { title: 'Application Info', desc: 'Enter the name of the journal, its details and the editorial board members.' },
  { title: 'Upload Files', desc: 'Upload three PDFs (max. 20MB each): the Journal Application, the Publication & Operational Guidelines for Authors, and the Journal Policy.' },
  { title: 'Review & Submit', desc: 'Check your application and submit. Track its progress from your dashboard.' },
];

const SubmissionCard: React.FC<{ icon: React.ReactNode; role: string; heading: string; steps: Step[] }> = ({ icon, role, heading, steps }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-1">
      <div className="bg-green-50 text-[#088A61] p-2 rounded-lg">{icon}</div>
      <span className="text-xs font-semibold uppercase tracking-wider text-[#088A61]">{role}</span>
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mt-3 mb-4">{heading}</h3>
    <ol className="space-y-4">
      {steps.map((step, i) => (
        <li key={step.title} className="flex gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#088A61] text-white text-sm font-semibold flex items-center justify-center">
            {i + 1}
          </span>
          <div>
            <p className="font-medium text-gray-900">{step.title}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

const GetStarted: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans">
      <header className="bg-[#088A61] text-white">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="flex items-center justify-between mb-10">
            <div className="bg-white p-2 rounded-md">
              <img src={TetfundLogo} alt="Logo" className="h-8 w-auto" />
            </div>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm font-medium opacity-90 hover:opacity-100">
              <ArrowLeft size={16} /> Back to sign in
            </Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-normal leading-tight mb-3">
            How to Sign Up &amp; Submit
          </h1>
          <p className="text-lg opacity-90 font-light max-w-2xl">
            Register as a Manuscript Author to submit a manuscript for academic book publication, or as a
            Professional Journal Representative to apply for professional journal support.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-14">
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">1. Sign up</h2>
          <p className="text-gray-600 mb-6">The same steps apply to authors and journal representatives.</p>
          <div className="grid gap-4 md:grid-cols-2">
            {signupSteps.map((step, i) => (
              <div key={step.title} className="bg-white rounded-xl border border-gray-200 p-5 flex gap-4">
                <div className="flex-shrink-0 bg-green-50 text-[#088A61] w-10 h-10 rounded-lg flex items-center justify-center">
                  {step.icon}
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 mb-0.5">STEP {i + 1}</p>
                  <p className="font-semibold text-gray-900 mb-1">{step.title}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">2. Make your submission</h2>
          <p className="text-gray-600 mb-6">
            From your dashboard, start a new submission. Each one goes through three short steps.
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            <SubmissionCard
              icon={<BookOpen size={20} />}
              role="Manuscript Author"
              heading="Submit a manuscript"
              steps={authorSteps}
            />
            <SubmissionCard
              icon={<Newspaper size={20} />}
              role="Journal Representative"
              heading="Apply for journal support"
              steps={journalSteps}
            />
          </div>
        </section>

        <section className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <CheckCircle2 size={32} className="mx-auto text-[#088A61] mb-3" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ready to begin?</h2>
          <p className="text-gray-600 mb-6">Create your account or sign in to continue.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 bg-[#088A61] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#067451] transition-all"
            >
              <UserPlus size={18} /> Create account
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-lg border-2 border-[#088A61] text-[#088A61] font-semibold hover:bg-green-50 transition-all"
            >
              <LogIn size={18} /> Sign in
            </Link>
          </div>
        </section>
      </main>

      <footer className="text-center text-sm text-gray-500 pb-10">
        © {new Date().getFullYear()} TETFund TAG. All rights reserved.
      </footer>
    </div>
  );
};

export default GetStarted;
